import { useEffect, useRef, useState } from "react";
import useOrientation from "@/hooks/useOrientation";
import { Toast } from "antd-mobile";
import useKeyboardControl from "./hooks/useKeyboardControl";
import { useCallAdapter } from "./hooks/useCallAdapter";
import CallController from "./components/CallController";
import MoveController, { EControlType } from "./components/MoveController";
import EmojiPanel from "./components/EmojiPanel";
import { ECallProvider } from "@/common/enums";

// 30697737/91534346 我手机 
// 95081902/17022023 我家 老孟家
// 11078136/22625788 代策1
// 54944215/96727444 代策2
// 22405375/22618897

enum ECallStatus {
  isCalling,
  isSpeaking,
  isVideoing,
  hangup,
}

const CallPage = () => {
  const orientation = useOrientation();
  // const windowHeight = useWindowHeight();
  const { setCallAdapter } = useKeyboardControl();

  const videoContainerRef = useRef<HTMLDivElement>(null);
  const localVideoRef = useRef<HTMLDivElement | HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLDivElement | HTMLVideoElement>(null);
  const remoteFullVideoRef = useRef<HTMLVideoElement>(null);
  const appElement = useRef(null);

  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showControllers, setShowControllers] = useState(true);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // 使用统一适配器 hook
  const {
    role,
    status,
    isEnteredRoom,
    isVideoOpen,
    countdown,
    callSeconds,
    showEmojiPanel,
    useFrontCamera,
    isTrackOn,
    isFullScreen,
    formatTime,
    adapter,
    calling,
    handleVideoToggle,
    handleHangup,
    handleTrackClick,
    handleShowEmoji,
    setShowEmojiPanel,
    setUseFrontCamera,
    setIsFullScreen,
    renderVideoElements,
  } = useCallAdapter();

  // 进入全屏
  const enterFullScreen = () => {
    setIsFullScreen(true);
    const element = appElement.current || document.documentElement;

    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if ((element as any).webkitRequestFullscreen) {
      // Safari
      (element as any).webkitRequestFullscreen();
    } else if ((element as any).mozRequestFullScreen) {
      // Firefox
      (element as any).mozRequestFullScreen();
    } else if ((element as any).msRequestFullscreen) {
      // IE/Edge
      (element as any).msRequestFullscreen();
    }
  };

  // 退出全屏
  const exitFullScreen = () => {
    setIsFullScreen(false);
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).mozExitFullscreen) {
      (document as any).mozExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
  };

  const showAllControllers = () => {
    setShowControllers(true);
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  };

  // 监听触屏事件
  useEffect(() => {
    const resetInactivityTimer = () => {
      console.log("resetInactivityTimer");
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(() => {
        if (status === ECallStatus.isVideoing) {
          setShowControllers(false);
        }
      }, 3000);
    };

    if (status !== ECallStatus.isVideoing) {
      return;
    }

    if (orientation === "portrait") {
      setShowControllers(true);
      window.removeEventListener("touchstart", showAllControllers);
      window.removeEventListener("touchend", resetInactivityTimer);
      window.removeEventListener("mousedown", showAllControllers);
      window.removeEventListener("mouseup", resetInactivityTimer);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      return;
    }

    resetInactivityTimer();

    window.addEventListener("touchstart", showAllControllers);
    window.addEventListener("touchend", resetInactivityTimer);
    window.addEventListener("mousedown", showAllControllers);
    window.addEventListener("mouseup", resetInactivityTimer);
    return () => {
      window.removeEventListener("touchstart", showAllControllers);
      window.removeEventListener("touchend", resetInactivityTimer);
      window.removeEventListener("mousedown", showAllControllers);
      window.removeEventListener("mouseup", resetInactivityTimer);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [status, orientation]);

  // 视频截图功能
  const captureVideoFrameFrom = async (
    container: HTMLDivElement | HTMLVideoElement | null,
    isMirror = false
  ): Promise<string | null> => {
    if (!container) return null;

    const provider = adapter?.getProvider();

    if (provider === ECallProvider.Trtc) {
      // TRTC 可能使用 canvas 或 video
      const canvasEl = container.querySelector(
        "canvas"
      ) as HTMLCanvasElement | null;
      if (canvasEl) {
        try {
          if (isMirror) {
            const w =
              canvasEl.width || canvasEl.getBoundingClientRect().width || 0;
            const h =
              canvasEl.height || canvasEl.getBoundingClientRect().height || 0;
            if (!w || !h) return null;
            const temp = document.createElement("canvas");
            temp.width = Math.floor(w);
            temp.height = Math.floor(h);
            const tctx = temp.getContext("2d");
            if (!tctx) return null;
            tctx.translate(temp.width, 0);
            tctx.scale(-1, 1);
            tctx.drawImage(canvasEl, 0, 0, temp.width, temp.height);
            return temp.toDataURL("image/png");
          }
          return canvasEl.toDataURL("image/png");
        } catch (e) {
          const w =
            canvasEl.width || canvasEl.getBoundingClientRect().width || 0;
          const h =
            canvasEl.height || canvasEl.getBoundingClientRect().height || 0;
          if (!w || !h) return null;
          const temp = document.createElement("canvas");
          temp.width = Math.floor(w);
          temp.height = Math.floor(h);
          const tctx = temp.getContext("2d");
          if (!tctx) return null;
          if (isMirror) {
            tctx.translate(temp.width, 0);
            tctx.scale(-1, 1);
          }
          tctx.drawImage(canvasEl, 0, 0, temp.width, temp.height);
          return temp.toDataURL("image/png");
        }
      }
    }

    // 兼容 video 元素
    const videoElement = container instanceof HTMLVideoElement ? container : container.querySelector(
      "video"
    ) as HTMLVideoElement | null;
    if (videoElement) {
      const temp = document.createElement("canvas");
      temp.width = videoElement.videoWidth;
      temp.height = videoElement.videoHeight;
      const tctx = temp.getContext("2d");
      if (!tctx) return null;
      if (isMirror) {
        tctx.translate(temp.width, 0);
        tctx.scale(-1, 1);
      }
      tctx.drawImage(videoElement, 0, 0, temp.width, temp.height);
      return temp.toDataURL("image/png");
    }

    return null;
  };

  const handleCaptureScreen = async () => {
    try {
      const container = videoContainerRef.current;
      if (!container) return;

      // 捕获远端与本地视频帧
      const [remoteData, localData] = await Promise.all([
        captureVideoFrameFrom(remoteVideoRef.current),
        captureVideoFrameFrom(localVideoRef.current, true),
      ]);
      if (!remoteData) throw new Error("no remote video frame");

      const containerRect = container.getBoundingClientRect();
      const width = Math.max(1, Math.floor(containerRect.width));
      const height = Math.max(1, Math.floor(containerRect.height));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no canvas context");

      // 背景铺黑
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, width, height);

      // 绘制远端画面（cover 容器）
      const remoteImg = new Image();
      remoteImg.src = remoteData;
      await new Promise((resolve, reject) => {
        remoteImg.onload = resolve as any;
        remoteImg.onerror = reject as any;
      });
      {
        const aspectVideo = remoteImg.width / remoteImg.height;
        const aspectContainer = width / height;
        let drawWidth = width;
        let drawHeight = height;
        let offsetX = 0;
        let offsetY = 0;
        if (aspectVideo > aspectContainer) {
          drawHeight = height;
          drawWidth = drawHeight * aspectVideo;
          offsetX = (width - drawWidth) / 2;
        } else {
          drawWidth = width;
          drawHeight = drawWidth / aspectVideo;
          offsetY = (height - drawHeight) / 2;
        }
        ctx.drawImage(remoteImg, offsetX, offsetY, drawWidth, drawHeight);
      }

      // 叠加本地画面
      if (localData) {
        const localRect = localVideoRef.current?.getBoundingClientRect();
        if (localRect) {
          const localImg = new Image();
          localImg.src = localData;
          await new Promise((resolve, reject) => {
            localImg.onload = resolve as any;
            localImg.onerror = reject as any;
          });

          const targetX = Math.max(
            0,
            Math.floor(localRect.left - containerRect.left)
          );
          const targetY = Math.max(
            0,
            Math.floor(localRect.top - containerRect.top)
          );
          const targetW = Math.min(
            width - targetX,
            Math.floor(localRect.width)
          );
          const targetH = Math.min(
            height - targetY,
            Math.floor(localRect.height)
          );

          const aspectVideo = localImg.width / localImg.height;
          const aspectTarget = targetW / targetH;
          let drawW = targetW;
          let drawH = targetH;
          let offX = 0;
          let offY = 0;
          if (aspectVideo > aspectTarget) {
            drawH = targetH;
            drawW = drawH * aspectVideo;
            offX = (targetW - drawW) / 2;
          } else {
            drawW = targetW;
            drawH = drawW / aspectVideo;
            offY = (targetH - drawH) / 2;
          }
          ctx.save();
          ctx.beginPath();
          ctx.rect(targetX, targetY, targetW, targetH);
          ctx.clip();
          ctx.drawImage(localImg, targetX + offX, targetY + offY, drawW, drawH);
          ctx.restore();
        }
      }

      const date = new Date();
      const filename = `aw_${date.toISOString().replace(/[:.]/g, "-")}.png`;
      const dataUrl = canvas.toDataURL("image/png");

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      Toast.show({ content: "截图保存成功", duration: 2000 });
    } catch (error) {
      console.error("截图保存失败:", error);
      Toast.show({ content: "截图保存失败", duration: 2000 });
    }
  };

  // 切换摄像头
  const handleChangeCamera = async () => {
    try {
      const newCamera = !useFrontCamera;
      setUseFrontCamera(newCamera);

      if (adapter) {
        await adapter.switchCamera(newCamera, localVideoRef.current);
      }
    } catch (error) {
      console.error("切换摄像头失败:", error);
      Toast.show({
        content: "切换摄像头失败",
      });
    }
  };

  // 拖拽功能
  const handleDragStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    setIsDragging(true);
    e.preventDefault();
    let clientX, clientY;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setOffset({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });
  };

  const handleDrag = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (!isDragging) return;
    let clientX, clientY;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const containerRect = remoteVideoRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    const localVideoRect = localVideoRef.current?.getBoundingClientRect();
    if (!localVideoRect) return;

    let newX = clientX - offset.x - containerRect.left;
    let newY = clientY - offset.y - containerRect.top;

    newX = Math.max(
      0,
      Math.min(newX, containerRect.width - localVideoRect.width)
    );
    newY = Math.max(
      0,
      Math.min(newY, containerRect.height - localVideoRect.height)
    );

    setDragPosition({ x: newX, y: newY });
  };

  const handleDragEnd = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    setIsDragging(false);
    const remoteVideoRect = remoteVideoRef.current?.getBoundingClientRect();
    if (remoteVideoRect) {
      const localVideoRect = e.currentTarget.getBoundingClientRect();
      const centerX = localVideoRect.left + localVideoRect.width / 2;
      const isLeft = centerX < remoteVideoRect.left + remoteVideoRect.width / 2;
      setDragPosition({
        x: isLeft ? 0 : remoteVideoRect.width - localVideoRect.width,
        y: dragPosition.y,
      });
    }
  };

  // 初始化位置
  useEffect(() => {
    const remoteRect = remoteVideoRef.current?.getBoundingClientRect();
    const localRect = localVideoRef.current?.getBoundingClientRect();
    setDragPosition({
      x: (remoteRect?.width ?? 0) - (localRect?.width || 0),
      y: 0,
    });
  }, [orientation]);

  // 设置键盘控制
  useEffect(() => {
    if (adapter) {
      setCallAdapter(adapter);
    }
  }, [adapter, setCallAdapter]);

  useEffect(() => {
    calling();
  }, []);

  // 渲染敲门提示
  const renderKnockTip = () => {
    return (
      <div className="flex flex-col items-center justify-center z-10">
        <img
          className="w-[6.875rem] h-[4.625rem]"
          src={require("@/assets/images/door-bell.png")}
          alt=""
        />
        <div className="text-xl text-white mt-1">敲门中......</div>
      </div>
    );
  };

  return (
    <div
      ref={appElement}
      className={`landscape:relative flex flex-col items-center justify-between h-screen`}
    >
      {status === ECallStatus.isVideoing && (
        <div className="absolute top-0 left-0 flex flex-col items-center w-full h-full">
          <div ref={videoContainerRef} className="relative w-screen h-screen">
            {/* Remote video area */}
            <div className="absolute top-0 left-0 w-full h-full">
              {renderVideoElements("remote", remoteVideoRef, remoteFullVideoRef)}
            </div>
            {/* Local video */}
            {renderVideoElements(
              "local",
              localVideoRef,
              remoteFullVideoRef,
              dragPosition,
              isDragging,
              handleDragStart,
              handleDrag,
              handleDragEnd,
              handleChangeCamera
            )}
          </div>
        </div>
      )}

      <div className="mp-2 w-full flex flex-col items-center z-10">
        {status === ECallStatus.isVideoing && (
          <span
            className="text-white text-sm px-3 py-1 rounded select-none landscape:absolute landscape:top-1 landscape:left-1/2 landscape:transform landscape:-translate-x-1/2"
            style={{ letterSpacing: 2 }}
          >
            {formatTime(callSeconds)}
          </span>
        )}

        {status !== ECallStatus.isVideoing &&
          status !== ECallStatus.hangup &&
          role === 1 && (
            <div className="absolute top-2 flex flex-col items-center justify-center w-full">
              {status === ECallStatus.isCalling && (
                <div className="text-base text-white">
                  {countdown}秒后自动接入语音
                </div>
              )}
              {status === ECallStatus.isSpeaking && (
                <div className="text-base text-white">通话已接通</div>
              )}
            </div>
          )}
      </div>

      {status === ECallStatus.isCalling && !isEnteredRoom && renderKnockTip()}

      {showControllers && (
        <div className="w-full flex flex-col items-center justify-between">
          <CallController
            className={status === ECallStatus.isVideoing ? "xs:hidden" : "flex"}
            showVideoButton={role === 1 && status < ECallStatus.isVideoing}
            isTrackOn={isTrackOn}
            isFullScreen={isFullScreen}
            isVideoOpen={isVideoOpen}
            videoDisabled={status === ECallStatus.isCalling}
            onVideoToggle={handleVideoToggle}
            onHangup={handleHangup}
            handleTrackClick={handleTrackClick}
            handleShowEmoji={handleShowEmoji}
            handleFullScreenClick={
              isFullScreen ? exitFullScreen : enterFullScreen
            }
            handleCaptureScreen={handleCaptureScreen}
          />

          {status === ECallStatus.isVideoing ? (
            <div className="w-full flex items-center portrait:mb-10 z-10">
              <div className="w-full flex justify-between px-6 box-border landscape:justify-around landscape:absolute landscape:bottom-2">
                <MoveController
                  type={EControlType.Direction}
                  adapter={adapter || undefined}
                  isEnteredRoom={isEnteredRoom}
                />
                <CallController
                  className="hidden xs:flex"
                  showVideoButton={
                    role === 1 && status < ECallStatus.isVideoing
                  }
                  isTrackOn={isTrackOn}
                  isFullScreen={isFullScreen}
                  isVideoOpen={isVideoOpen}
                  onVideoToggle={handleVideoToggle}
                  onHangup={handleHangup}
                  handleTrackClick={handleTrackClick}
                  handleShowEmoji={handleShowEmoji}
                  handleFullScreenClick={
                    isFullScreen ? exitFullScreen : enterFullScreen
                  }
                  handleCaptureScreen={handleCaptureScreen}
                />
                <MoveController
                  type={EControlType.Neck}
                  adapter={adapter || undefined}
                  isEnteredRoom={isEnteredRoom}
                />
              </div>
            </div>
          ) : (
            <div className="w-full h-44 landscape:h-5" />
          )}
        </div>
      )}

      <EmojiPanel
        visible={showEmojiPanel}
        adapter={adapter || undefined}
        onClose={() => setShowEmojiPanel(false)}
      />
    </div>
  );
};

export default CallPage;
